import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Package,
  Search,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  Eye,
  Filter,
  Download,
  Upload,
  Save,
  X,
  Check,
  AlertCircle,
  BarChart3
} from 'lucide-react';
import { productsAPI, categoriesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currency';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernSelect,
  ModernTable,
  ModernBadge,
  ModernModal,
  ModernSearchBar,
  ModernActionBar,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';
import JsBarcode from 'jsbarcode';
import jsPDF from 'jspdf';
import EntitySelectWithAdd from '../components/ui/EntitySelectWithAdd';

const defaultForm = {
  name: '',
  brand: '',
  model: '',
  purchase_price: '',
  sale_price: '',
  stock: '',
  category_id: '',
  // Lens (verres) fields
  eye_side: '',
  sph: '',
  cyl: '',
  axis: '',
  addition: '',
  diameter: '',
  material: '',
  coating: '',
  index: '',
  tint: '',
  // Frame (montures) fields
  frame_size: '',
  bridge_size: '',
  temple_length: '',
  frame_material: '',
  frame_shape: '',
  frame_color: '',
  barcode: '',
  /** facture = achat avec facture fournisseur ; non_facture = sans facture (ex. noir) */
  achat_facture: 'facture',
};

const achatFactureFilterOptions = [
  { value: '', label: 'Tous' },
  { value: 'facture', label: 'Avec facture achat' },
  { value: 'non_facture', label: 'Sans facture achat' },
];

const achatFactureFormOptions = [
  { value: 'facture', label: 'Avec facture achat' },
  { value: 'non_facture', label: 'Sans facture achat (ex. noir)' },
];

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { hasAnyRole, isAdmin, isManager } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const fileInputRef = React.useRef();
  const [categories, setCategories] = useState([]);
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [barcodeProduct, setBarcodeProduct] = useState(null);
  const [barcodeCount, setBarcodeCount] = useState(1);
  const [showBarcodeSelectionModal, setShowBarcodeSelectionModal] = useState(false);
  const [selectedProductsForBarcode, setSelectedProductsForBarcode] = useState([]);
  const [barcodeSearchTerm, setBarcodeSearchTerm] = useState('');
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    brand: '',
    stockStatus: '',
    minPrice: '',
    maxPrice: '',
    material: '',
    frameType: '',
    eyeSide: '',
    frameColor: '',
    tint: '',
    achatFacture: ''
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productsAPI.getProducts();
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getCategories();
      setCategories(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des catégories');
    }
  };

  // Get unique brands from products
  const uniqueBrands = [...new Set(products.map(p => p.brand).filter(Boolean))];
  
  // Get unique materials from products
  const uniqueMaterials = [...new Set(products.map(p => p.material).filter(Boolean))];
  
  // Get unique frame types from products
  const uniqueFrameTypes = [...new Set(products.map(p => p.frame_material).filter(Boolean))];
  
  // Get unique frame colors from products
  const uniqueFrameColors = [...new Set(products.map(p => p.frame_color).filter(Boolean))];
  
  // Get unique tints from products
  const uniqueTints = [...new Set(products.map(p => p.tint).filter(Boolean))];

  const filteredProducts = products.filter(product => {
    // Text search filter
    const matchesSearch = !searchTerm || 
      String(product.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(product.category_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(product.brand || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(product.model || '').toLowerCase().includes(searchTerm.toLowerCase());

    // Category filter
    const matchesCategory = !filters.category || product.category_id == filters.category;

    // Brand filter
    const matchesBrand = !filters.brand || product.brand === filters.brand;

    // Stock status filter
    let matchesStockStatus = true;
    if (filters.stockStatus) {
      switch (filters.stockStatus) {
        case 'in_stock':
          matchesStockStatus = product.stock > 0;
          break;
        case 'out_of_stock':
          matchesStockStatus = product.stock <= 0;
          break;
        case 'low_stock':
          matchesStockStatus = product.stock > 0 && product.stock <= 10;
          break;
        default:
          matchesStockStatus = true;
      }
    }

    // Price range filter
    const matchesPriceRange = (!filters.minPrice || product.sale_price >= parseFloat(filters.minPrice)) &&
                             (!filters.maxPrice || product.sale_price <= parseFloat(filters.maxPrice));

    // Material filter
    const matchesMaterial = !filters.material || product.material === filters.material;

    // Frame type filter
    const matchesFrameType = !filters.frameType || product.frame_material === filters.frameType;

    // Eye side filter
    const matchesEyeSide = !filters.eyeSide || product.eye_side === filters.eyeSide;

    // Frame color filter
    const matchesFrameColor = !filters.frameColor || product.frame_color === filters.frameColor;

    // Tint filter
    const matchesTint = !filters.tint || product.tint === filters.tint;

    const af = product.achat_facture || 'facture';
    const matchesAchatFacture = !filters.achatFacture || af === filters.achatFacture;

    return matchesSearch && matchesCategory && matchesBrand && matchesStockStatus && 
           matchesPriceRange && matchesMaterial && matchesFrameType && matchesEyeSide &&
           matchesFrameColor && matchesTint && matchesAchatFacture;
  });

  const getStockStatus = (stock) => {
    if (stock <= 0) return { status: 'out', variant: 'danger', label: 'Rupture de stock' };
    if (stock <= 10) return { status: 'low', variant: 'warning', label: 'Stock faible' };
    return { status: 'good', variant: 'success', label: 'En stock' };
  };

  // Add/edit modal logic
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      brand: '',
      stockStatus: '',
      minPrice: '',
      maxPrice: '',
      material: '',
      frameType: '',
      eyeSide: '',
      frameColor: '',
      tint: '',
      achatFacture: ''
    });
    setSearchTerm('');
  };

  const hasActiveFilters = () => {
    return Object.values(filters).some(value => value !== '') || searchTerm !== '';
  };

  const getStockStatusLabel = (status) => {
    switch (status) {
      case 'in_stock': return 'En stock';
      case 'low_stock': return 'Stock faible';
      case 'out_of_stock': return 'Rupture de stock';
      default: return '';
    }
  };

  const getEyeSideLabel = (side) => {
    switch (side) {
      case 'left': return 'Gauche';
      case 'right': return 'Droite';
      case 'both': return 'Les deux';
      default: return '';
    }
  };

  const handleAdd = () => {
    // Check if user has permission to add products
    if (!isAdmin() && !isManager()) {
      toast.error('Vous n\'avez pas les permissions pour ajouter des produits');
      return;
    }
    
    setForm({ ...defaultForm, category_id: categories[0]?.id || '' });
    setEditingId(null);
    setIsViewMode(false);
    setShowForm(true);
  };

  const handleView = (product) => {
    setForm({
      name: product.name,
      brand: product.brand || '',
      model: product.model || '',
      purchase_price: product.purchase_price,
      sale_price: product.sale_price,
      stock: product.stock,
      category_id: product.category_id || '',
      eye_side: product.eye_side || '',
      sph: product.sph || '',
      cyl: product.cyl || '',
      axis: product.axis || '',
      addition: product.addition || '',
      diameter: product.diameter || '',
      material: product.material || '',
      coating: product.coating || '',
      index: product.index || '',
      tint: product.tint || '',
      frame_size: product.frame_size || '',
      bridge_size: product.bridge_size || '',
      temple_length: product.temple_length || '',
      frame_material: product.frame_material || '',
      frame_shape: product.frame_shape || '',
      frame_color: product.frame_color || '',
      barcode: product.barcode || '',
      achat_facture: product.achat_facture || 'facture',
    });
    setEditingId(null);
    setIsViewMode(true);
    setShowForm(true);
  };

  const handleEdit = (product) => {
    // Check if user has permission to edit products
    if (!isAdmin() && !isManager()) {
      toast.error('Vous n\'avez pas les permissions pour modifier les produits');
      return;
    }
    
    setForm({
      name: product.name,
      brand: product.brand || '',
      model: product.model || '',
      purchase_price: product.purchase_price,
      sale_price: product.sale_price,
      stock: product.stock,
      category_id: product.category_id || '',
      eye_side: product.eye_side || '',
      sph: product.sph || '',
      cyl: product.cyl || '',
      axis: product.axis || '',
      addition: product.addition || '',
      diameter: product.diameter || '',
      material: product.material || '',
      coating: product.coating || '',
      index: product.index || '',
      tint: product.tint || '',
      frame_size: product.frame_size || '',
      bridge_size: product.bridge_size || '',
      temple_length: product.temple_length || '',
      frame_material: product.frame_material || '',
      frame_shape: product.frame_shape || '',
      frame_color: product.frame_color || '',
      barcode: product.barcode || '',
      achat_facture: product.achat_facture || 'facture',
    });
    setEditingId(product.id);
    setIsViewMode(false);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    // Check if user has permission to delete products
    if (!isAdmin() && !isManager()) {
      toast.error('Vous n\'avez pas les permissions pour supprimer les produits');
      return;
    }
    
    if (!window.confirm('Supprimer ce produit ?')) return;
    try {
      await productsAPI.deleteProduct(id);
      toast.success('Produit supprimé');
      fetchProducts();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Function to clean form data - send empty strings for optional fields
  const cleanFormData = (formData) => {
    const cleanedData = { ...formData };
    
    // Lens (verres) fields - send empty string if empty
    const lensFields = ['eye_side', 'sph', 'cyl', 'axis', 'addition', 'diameter', 'material', 'coating', 'index', 'tint'];
    lensFields.forEach(field => {
      if (cleanedData[field] === '' || cleanedData[field] === null || cleanedData[field] === undefined) {
        cleanedData[field] = '';
      }
    });
    
    // Frame (montures) fields - send empty string if empty
    const frameFields = ['frame_size', 'bridge_size', 'temple_length', 'frame_material', 'frame_shape', 'frame_color'];
    frameFields.forEach(field => {
      if (cleanedData[field] === '' || cleanedData[field] === null || cleanedData[field] === undefined) {
        cleanedData[field] = '';
      }
    });
    
    // Other optional fields - send empty string if empty
    if (cleanedData.brand === '' || cleanedData.brand === null || cleanedData.brand === undefined) cleanedData.brand = '';
    if (cleanedData.model === '' || cleanedData.model === null || cleanedData.model === undefined) cleanedData.model = '';
    if (cleanedData.barcode === '' || cleanedData.barcode === null || cleanedData.barcode === undefined) cleanedData.barcode = '';
    
    return cleanedData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent submission in view mode
    if (isViewMode) {
      return;
    }
    
    // Check if user has permission to modify products
    if (!isAdmin() && !isManager()) {
      toast.error('Vous n\'avez pas les permissions pour modifier les produits');
      return;
    }
    
    setSubmitLoading(true);
    try {
      // Clean the form data before submission
      const cleanedFormData = cleanFormData(form);
      console.log('Submitting cleaned form:', cleanedFormData); // Debug log
      
      if (editingId) {
        await productsAPI.updateProduct(editingId, cleanedFormData);
        toast.success('Produit modifié');
      } else {
        await productsAPI.createProduct(cleanedFormData);
        toast.success('Produit ajouté');
      }
      setShowForm(false);
      setForm(defaultForm);
      setEditingId(null);
      setIsViewMode(false);
      fetchProducts();
    } catch (err) {
      console.error('Submit error:', err); // Debug log
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Excel Export
  const handleExport = async () => {
    // Check if user has permission to export products
    if (!isAdmin() && !isManager()) {
      toast.error('Vous n\'avez pas les permissions pour exporter les produits');
      return;
    }
    
    try {
      const response = await productsAPI.exportProducts();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'products.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success('Export Excel réussi');
    } catch (err) {
      toast.error('Erreur lors de l\'export Excel');
    }
  };

  // Excel Import
  const handleImport = async (e) => {
    // Check if user has permission to import products
    if (!isAdmin() && !isManager()) {
      toast.error('Vous n\'avez pas les permissions pour importer des produits');
      return;
    }
    
    const file = e.target.files[0];
    if (!file) return;
    setImportLoading(true);
    try {
      const response = await productsAPI.importProducts(file);
      toast.success(response.data.message || 'Import Excel réussi');
      fetchProducts();
    } catch (err) {
      toast.error('Erreur lors de l\'import Excel');
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePrintBarcode = (product) => {
    setBarcodeProduct(product);
    setBarcodeCount(1);
    setShowBarcodeModal(true);
  };

  const handlePrintMultipleBarcodes = () => {
    setSelectedProductsForBarcode([]);
    setBarcodeSearchTerm('');
    setShowBarcodeSelectionModal(true);
  };

  const addProductToBarcodeSelection = (product) => {
    const existingProduct = selectedProductsForBarcode.find(p => p.id === product.id);
    if (existingProduct) {
      setSelectedProductsForBarcode(prev => 
        prev.map(p => p.id === product.id ? { ...p, quantity: (p.quantity || 1) + 1 } : p)
      );
    } else {
      setSelectedProductsForBarcode(prev => [...prev, { ...product, quantity: 1 }]);
    }
  };

  const removeProductFromBarcodeSelection = (productId) => {
    setSelectedProductsForBarcode(prev => prev.filter(p => p.id !== productId));
  };

  const updateProductQuantity = (productId, quantity) => {
    setSelectedProductsForBarcode(prev => 
      prev.map(p => p.id === productId ? { ...p, quantity: Math.max(1, quantity) } : p)
    );
  };

  const generateMultipleBarcodePDF = () => {
    if (selectedProductsForBarcode.length === 0) return;
    
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 2;
    const cellWidth = 41;
    const cellHeight = 22;
    const spacing = 0.5;
    
    let currentPage = 0;
    let barcodesOnCurrentPage = 0;
    const maxBarcodesPerPage = 65;
    const columns = 5;
    const rows = 13;
    
    // Calculate total barcodes needed
    const totalBarcodes = selectedProductsForBarcode.reduce((total, product) => total + (product.quantity || 1), 0);
    let barcodeIndex = 0;
    
    selectedProductsForBarcode.forEach(product => {
      const totalBarcodesForProduct = product.quantity || 1;
      
      for (let i = 0; i < totalBarcodesForProduct; i++) {
        // Check if we need a new page
        if (barcodesOnCurrentPage >= maxBarcodesPerPage) {
          doc.addPage();
          currentPage++;
          barcodesOnCurrentPage = 0;
        }
        
        // Calculate position on current page (5 columns, 13 rows)
        const row = Math.floor(barcodesOnCurrentPage / columns);
        const col = barcodesOnCurrentPage % columns;
        const x = margin + col * (cellWidth + spacing);
        const y = margin + row * (cellHeight + spacing);
        
        // Check if we need a new page due to vertical space
        if (y + cellHeight > pageHeight - margin) {
          doc.addPage();
          currentPage++;
          barcodesOnCurrentPage = 0;
          const newRow = 0;
          const newCol = 0;
          const newX = margin + newCol * (cellWidth + spacing);
          const newY = margin + newRow * (cellHeight + spacing);
          
          // Create barcode
          const canvas = document.createElement('canvas');
          JsBarcode(canvas, product.barcode || product.id.toString(), {
            format: 'CODE128',
            width: 1,
            height: 10,
            displayValue: false,
            margin: 0,
            background: '#ffffff'
          });
          const imgData = canvas.toDataURL('image/png');
          doc.addImage(imgData, 'PNG', newX + 1, newY + 1, cellWidth - 2, 10);
          
          // Add product info below barcode with clean formatting
          doc.setFontSize(4);
          doc.setFont('helvetica', 'normal');
          
          // Product name (first line)
          const productName = product.name || 'Produit';
          const truncatedName = productName.length > 12 ? productName.substring(0, 12) : productName;
          doc.text(truncatedName, newX + cellWidth / 2, newY + 13, { align: 'center' });
          
          // Price (second line) - format as "230 MAD"
          const price = product.sale_price || 0;
          const priceText = `${price} MAD`;
          doc.text(priceText, newX + cellWidth / 2, newY + 16, { align: 'center' });
          
          // Reference (third line) if available
          if (product.reference) {
            const refText = product.reference.length > 8 ? product.reference.substring(0, 8) : product.reference;
            doc.text(`Ref: ${refText}`, newX + cellWidth / 2, newY + 19, { align: 'center' });
          }
        } else {
          // Create barcode
          const canvas = document.createElement('canvas');
          JsBarcode(canvas, product.barcode || product.id.toString(), {
            format: 'CODE128',
            width: 1,
            height: 10,
            displayValue: false,
            margin: 0,
            background: '#ffffff'
          });
          const imgData = canvas.toDataURL('image/png');
          doc.addImage(imgData, 'PNG', x + 1, y + 1, cellWidth - 2, 10);
          
          // Add product info below barcode with clean formatting
          doc.setFontSize(4);
          doc.setFont('helvetica', 'normal');
          
          // Product name (first line)
          const productName = product.name || 'Produit';
          const truncatedName = productName.length > 12 ? productName.substring(0, 12) : productName;
          doc.text(truncatedName, x + cellWidth / 2, y + 13, { align: 'center' });
          
          // Price (second line) - format as "230 MAD"
          const price = product.sale_price || 0;
          const priceText = `${price} MAD`;
          doc.text(priceText, x + cellWidth / 2, y + 16, { align: 'center' });
          
          // Reference (third line) if available
          if (product.reference) {
            const refText = product.reference.length > 8 ? product.reference.substring(0, 8) : product.reference;
            doc.text(`Ref: ${refText}`, x + cellWidth / 2, y + 19, { align: 'center' });
          }
        }
        
        barcodesOnCurrentPage++;
        barcodeIndex++;
      }
    });
    
    doc.save('codes_barres_etiquettes.pdf');
    setShowBarcodeSelectionModal(false);
    setSelectedProductsForBarcode([]);
  };

  const generateBarcodePDF = () => {
    if (!barcodeProduct || !barcodeProduct.barcode) return;
    const count = Math.max(1, barcodeCount);
    const barcodesPerRow = 4;
    const barcodeWidth = 60;
    const barcodeHeight = 30;
    const margin = 10;
    const labelHeight = 8;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    let x = margin, y = margin;
    let rowCount = 0;
    for (let i = 0; i < count; i++) {
      // Create a canvas for JsBarcode
      const canvas = document.createElement('canvas');
      JsBarcode(canvas, barcodeProduct.barcode, {
        format: 'CODE128',
        width: 1.5,
        height: barcodeHeight,
        displayValue: false,
        margin: 0
      });
      const imgData = canvas.toDataURL('image/png');
      doc.addImage(imgData, 'PNG', x, y, barcodeWidth, barcodeHeight);
      // Add product name below barcode
      doc.setFontSize(8);
      doc.text(barcodeProduct.name || '', x + barcodeWidth / 2, y + barcodeHeight + labelHeight, { align: 'center' });
      x += barcodeWidth + margin;
      rowCount++;
      if (rowCount >= barcodesPerRow) {
        rowCount = 0;
        x = margin;
        y += barcodeHeight + labelHeight + margin;
        // Add new page if needed
        if (y + barcodeHeight + labelHeight + margin > 297 - margin) {
          doc.addPage();
          y = margin;
        }
      }
    }
    doc.save(`barcodes_${barcodeProduct.name || ''}.pdf`);
    setShowBarcodeModal(false);
  };



  const tableColumns = [
    {
      key: 'name',
      label: 'Produit',
      render: (value, product) => (
        <div className="flex items-center">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
            <Package className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <div className="font-semibold text-neutral-900">{product.name}</div>
            {product.model && (
              <div className="text-sm text-neutral-500">Modèle: {product.model}</div>
            )}
          </div>
        </div>
      )
    },

    { key: 'brand', label: 'Marque' },
    {
      key: 'purchase_price',
      label: "Prix d'achat",
      render: (value) => (
        <span className="font-semibold text-blue-600">{formatCurrency(value)}</span>
      )
    },
    {
      key: 'sale_price',
      label: 'Prix de vente',
      render: (value) => (
        <span className="font-semibold text-green-600">{formatCurrency(value)}</span>
      )
    },
    {
      key: 'stock',
      label: 'Stock',
      render: (value) => {
        const status = getStockStatus(value);
        return (
          <div className="flex items-center space-x-2">
            <ModernBadge variant={status.variant}>
              {status.label}
            </ModernBadge>
            <span className="text-sm text-neutral-600">({value})</span>
          </div>
        );
      }
    },
    {
      key: 'qty_sold',
      label: 'Vendu',
      render: (value, product) => {
        const n = Number(product.qty_sold);
        const qty = Number.isFinite(n) ? n : 0;
        return (
          <div className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
              <TrendingUp className="h-4 w-4" aria-hidden />
            </span>
            <span className="font-semibold tabular-nums text-neutral-800">{qty}</span>
          </div>
        );
      }
    },
    {
      key: 'barcode',
      label: 'Code-barres',
      render: (value) => value || <span className="text-neutral-400">-</span>
    },
    {
      key: 'category_name',
      label: 'Catégorie',
      render: (value) => (
        <ModernBadge variant="secondary">{value}</ModernBadge>
      )
    },
    {
      key: 'achat_facture',
      label: 'Facture achat',
      render: (value) => (
        <ModernBadge variant={value === 'non_facture' ? 'warning' : 'success'}>
          {value === 'non_facture' ? 'Sans facture' : 'Avec facture'}
        </ModernBadge>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, product) => (
        <div className="flex items-center space-x-2">
          <ModernButton
            variant="ghost"
            size="sm"
            icon={<Eye className="w-4 h-4" />}
            onClick={() => isAdmin() || isManager() ? handleEdit(product) : handleView(product)}
          >
            Voir
          </ModernButton>
          {(isAdmin() || isManager()) && (
            <ModernButton
              variant="primary"
              size="sm"
              icon={<Edit className="w-4 h-4" />}
              onClick={() => handleEdit(product)}
            >
              Modifier
            </ModernButton>
          )}
          {(isAdmin() || isManager()) && (
            <ModernButton
              variant="danger"
              size="sm"
              icon={<Trash2 className="w-4 h-4" />}
              onClick={() => handleDelete(product.id)}
            >
              Supprimer
            </ModernButton>
          )}
        </div>
      )
    }
  ];

  // Helper to get selected category name
  const selectedCategory = categories.find(c => String(c.id) === String(form.category_id));
  const selectedCategoryName = selectedCategory ? selectedCategory.name.toLowerCase() : '';
  const showLensFields = selectedCategoryName.includes('verre');
  const showFrameFields = selectedCategoryName.includes('monture');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Produits</h1>
          <p className="text-neutral-600">Gérez votre inventaire de produits</p>
        </div>
        <div className="flex items-center space-x-3">
          <ModernSearchBar
            placeholder="Rechercher des produits..."
            onSearch={setSearchTerm}
            className="w-64"
            debounce={300}
          />
          {(isAdmin() || isManager()) && (
            <ModernButton
              variant="primary"
              icon={<Plus className="w-4 h-4" />}
              onClick={handleAdd}
            >
              Ajouter un produit
            </ModernButton>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Produits</p>
              <p className="text-2xl font-bold text-neutral-900">{products.length}</p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">En Stock</p>
              <p className="text-2xl font-bold text-success-600">
                {products.filter(p => p.stock > 0).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <Check className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Stock Faible</p>
              <p className="text-2xl font-bold text-warning-600">
                {products.filter(p => p.stock <= 10 && p.stock > 0).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Rupture</p>
              <p className="text-2xl font-bold text-danger-600">
                {products.filter(p => p.stock <= 0).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-danger-100 rounded-lg flex items-center justify-center">
              <X className="w-6 h-6 text-danger-600" />
            </div>
          </div>
        </ModernCard>
      </div>

      {/* Products Table */}
      <ModernCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Liste des produits</h3>
            <p className="text-sm text-neutral-600">{filteredProducts.length} produits trouvés</p>
          </div>
          <div className="flex items-center space-x-2">
            <ModernButton 
              variant="ghost" 
              size="sm" 
              icon={<Filter className="w-4 h-4" />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filtrer {hasActiveFilters() && <span className="ml-1 bg-primary-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center">
                {Object.values(filters).filter(v => v !== '').length + (searchTerm ? 1 : 0)}
              </span>}
            </ModernButton>
            {(isAdmin() || isManager()) && (
              <ModernButton
                variant="ghost"
                size="sm"
                icon={<Download className="w-4 h-4" />}
                onClick={handleExport}
              >
                Exporter
              </ModernButton>
            )}
            {(isAdmin() || isManager()) && (
              <ModernButton
                variant="ghost"
                size="sm"
                icon={<Upload className="w-4 h-4" />}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                disabled={importLoading}
              >
                {importLoading ? 'Import...' : 'Importer'}
              </ModernButton>
            )}
            <ModernButton
              variant="primary"
              size="sm"
              icon={<Package className="w-4 h-4" />}
              onClick={handlePrintMultipleBarcodes}
            >
              Imprimer codes-barres
            </ModernButton>
            {(isAdmin() || isManager()) && (
              <ModernButton
                variant="secondary"
                size="sm"
                icon={<BarChart3 className="w-4 h-4" />}
                onClick={() => window.location.href = '/stock-movements'}
              >
                Mouvements de stock
              </ModernButton>
            )}
            {(isAdmin() || isManager()) && (
              <input
                type="file"
                accept=".xlsx, .xls"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleImport}
              />
            )}
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mb-6 p-4 bg-neutral-50 rounded-lg border">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-medium text-neutral-900">Filtres avancés</h4>
              <ModernButton
                variant="ghost"
                size="sm"
                icon={<X className="w-4 h-4" />}
                onClick={clearFilters}
              >
                Effacer tous les filtres
              </ModernButton>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Category Filter */}
              <ModernSelect
                label="Catégorie"
                value={filters.category ? { value: filters.category, label: categories.find(c => c.id == filters.category)?.name || '' } : null}
                onChange={(option) => handleFilterChange('category', option?.value || '')}
                options={[
                  { value: '', label: 'Toutes les catégories' },
                  ...categories.map(category => ({ value: category.id, label: category.name }))
                ]}
                isClearable
              />

              {/* Brand Filter */}
              <ModernSelect
                label="Marque"
                value={filters.brand ? { value: filters.brand, label: filters.brand } : null}
                onChange={(option) => handleFilterChange('brand', option?.value || '')}
                options={[
                  { value: '', label: 'Toutes les marques' },
                  ...uniqueBrands.map(brand => ({ value: brand, label: brand }))
                ]}
                isClearable
              />

              {/* Stock Status Filter */}
              <ModernSelect
                label="Statut du stock"
                value={filters.stockStatus ? { value: filters.stockStatus, label: getStockStatusLabel(filters.stockStatus) } : null}
                onChange={(option) => handleFilterChange('stockStatus', option?.value || '')}
                options={[
                  { value: '', label: 'Tous les statuts' },
                  { value: 'in_stock', label: 'En stock' },
                  { value: 'low_stock', label: 'Stock faible' },
                  { value: 'out_of_stock', label: 'Rupture de stock' }
                ]}
                isClearable
              />

              <ModernSelect
                label="Facture d'achat"
                value={filters.achatFacture ? achatFactureFilterOptions.find(o => o.value === filters.achatFacture) || null : null}
                onChange={(option) => handleFilterChange('achatFacture', option?.value || '')}
                options={achatFactureFilterOptions}
                isClearable
              />

              {/* Eye Side Filter */}
              <ModernSelect
                label="Côté de l'œil"
                value={filters.eyeSide ? { value: filters.eyeSide, label: getEyeSideLabel(filters.eyeSide) } : null}
                onChange={(option) => handleFilterChange('eyeSide', option?.value || '')}
                options={[
                  { value: '', label: 'Tous les côtés' },
                  { value: 'left', label: 'Gauche' },
                  { value: 'right', label: 'Droite' },
                  { value: 'both', label: 'Les deux' }
                ]}
                isClearable
              />

              {/* Material Filter */}
              <ModernSelect
                label="Matériau"
                value={filters.material ? { value: filters.material, label: filters.material } : null}
                onChange={(option) => handleFilterChange('material', option?.value || '')}
                options={[
                  { value: '', label: 'Tous les matériaux' },
                  ...uniqueMaterials.map(material => ({ value: material, label: material }))
                ]}
                isClearable
              />

              {/* Frame Type Filter */}
              <ModernSelect
                label="Type de monture"
                value={filters.frameType ? { value: filters.frameType, label: filters.frameType } : null}
                onChange={(option) => handleFilterChange('frameType', option?.value || '')}
                options={[
                  { value: '', label: 'Tous les types' },
                  ...uniqueFrameTypes.map(frameType => ({ value: frameType, label: frameType }))
                ]}
                isClearable
              />

              {/* Min Price Filter */}
              <ModernInput
                label="Prix minimum"
                type="number"
                value={filters.minPrice}
                onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                placeholder="0.00"
              />

              {/* Max Price Filter */}
              <ModernInput
                label="Prix maximum"
                type="number"
                value={filters.maxPrice}
                onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                placeholder="9999.99"
              />

              {/* Frame Color Filter */}
              <ModernSelect
                label="Couleur de monture"
                value={filters.frameColor ? { value: filters.frameColor, label: filters.frameColor } : null}
                onChange={(option) => handleFilterChange('frameColor', option?.value || '')}
                options={[
                  { value: '', label: 'Toutes les couleurs' },
                  ...uniqueFrameColors.map(color => ({ value: color, label: color }))
                ]}
                isClearable
              />

              {/* Tint Filter */}
              <ModernSelect
                label="Teinte"
                value={filters.tint ? { value: filters.tint, label: filters.tint } : null}
                onChange={(option) => handleFilterChange('tint', option?.value || '')}
                options={[
                  { value: '', label: 'Toutes les teintes' },
                  ...uniqueTints.map(tint => ({ value: tint, label: tint }))
                ]}
                isClearable
              />
            </div>
          </div>
        )}

        <ModernTable
          columns={tableColumns}
          data={filteredProducts}
          className="w-full"
        />
      </ModernCard>

      {/* Add/Edit Modal */}
      <ModernModal
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setIsViewMode(false);
          setForm(defaultForm);
          setEditingId(null);
        }}
        title={
          isViewMode 
            ? 'Détails du produit' 
            : editingId 
              ? 'Modifier le produit' 
              : 'Ajouter un produit'
        }
        size="full"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Optional fields note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> Les champs marqués avec <span className="text-blue-600">(Optionnel)</span> sont facultatifs. 
              Si vous ne les remplissez pas, ils seront automatiquement définis comme vides.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ModernInput
              label="Nom du produit"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Ex: Lunettes de vue classiques"
              required
              disabled={isViewMode}
            />
            <ModernInput
              label="Marque (Optionnel)"
              name="brand"
              value={form.brand}
              onChange={handleChange}
              placeholder="Ex: Ray-Ban"
              disabled={isViewMode}
            />
            <ModernInput
              label="Modèle (Optionnel)"
              name="model"
              value={form.model}
              onChange={handleChange}
              placeholder="Ex: RB3025"
              disabled={isViewMode}
            />
            <ModernInput
              label="Prix d'achat"
              name="purchase_price"
              type="number"
              value={form.purchase_price}
              onChange={handleChange}
              placeholder="0.00"
              required
              disabled={isViewMode}
            />
            <ModernInput
              label="Prix de vente"
              name="sale_price"
              type="number"
              value={form.sale_price}
              onChange={handleChange}
              placeholder="0.00"
              required
              disabled={isViewMode}
            />
            <ModernInput
              label="Stock"
              name="stock"
              type="number"
              min="0"
              value={form.stock}
              onChange={handleChange}
              placeholder="0"
              required
              disabled={isViewMode}
            />
            {/* Replace ModernSelect for Category with EntitySelectWithAdd */}
            <EntitySelectWithAdd
              entityType="category"
              label="Catégorie"
              options={categories.map(c => ({ value: c.id, label: c.name }))}
              value={form.category_id}
              onChange={value => setForm(f => ({ ...f, category_id: value }))}
              fetchOptions={fetchCategories} // assumes you have a fetchCategories function
              required
              disabled={isViewMode}
            />
            <ModernSelect
              label="Facture d'achat"
              value={achatFactureFormOptions.find(o => o.value === (form.achat_facture || 'facture')) || achatFactureFormOptions[0]}
              onChange={(option) => setForm(f => ({ ...f, achat_facture: option?.value || 'facture' }))}
              options={achatFactureFormOptions}
              disabled={isViewMode}
            />
            <ModernInput
              label="Code-barres (Optionnel)"
              name="barcode"
              value={form.barcode}
              onChange={handleChange}
              placeholder="Ex: 1234567890123"
              disabled={isViewMode}
            />
          </div>

          {/* Opticien-specific fields, shown conditionally by category */}
          {showLensFields && (
            <div className="col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6 border-t pt-4 mt-4">
              <div className="font-semibold text-primary-700 md:col-span-2">Détails Verres (Optionnel)</div>
              <ModernSelect
                label="Côté de l'œil"
                name="eye_side"
                value={form.eye_side}
                onChange={handleChange}
                options={[{ value: '', label: '---' }, { value: 'left', label: 'Gauche' }, { value: 'right', label: 'Droit' }, { value: 'both', label: 'Les deux' }]}
                disabled={isViewMode}
              />
              <ModernInput label="Sphère (Sph)" name="sph" value={form.sph} onChange={handleChange} placeholder="Ex: -2.00" type="number" step="0.01" disabled={isViewMode} />
              <ModernInput label="Cylindre (Cyl)" name="cyl" value={form.cyl} onChange={handleChange} placeholder="Ex: -0.75" type="number" step="0.01" disabled={isViewMode} />
              <ModernInput label="Axe" name="axis" value={form.axis} onChange={handleChange} placeholder="Ex: 90" type="number" step="0.01" disabled={isViewMode} />
              <ModernInput label="Addition" name="addition" value={form.addition} onChange={handleChange} placeholder="Ex: 2.50" type="number" step="0.01" disabled={isViewMode} />
              <ModernInput label="Diamètre (mm)" name="diameter" value={form.diameter} onChange={handleChange} placeholder="Ex: 70" type="number" disabled={isViewMode} />
              <ModernInput label="Matériau" name="material" value={form.material} onChange={handleChange} placeholder="Ex: Polycarbonate" disabled={isViewMode} />
              <ModernInput label="Traitement" name="coating" value={form.coating} onChange={handleChange} placeholder="Ex: Anti-reflet" disabled={isViewMode} />
              <ModernInput label="Indice" name="index" value={form.index} onChange={handleChange} placeholder="Ex: 1.6" disabled={isViewMode} />
              <ModernInput label="Teinte" name="tint" value={form.tint} onChange={handleChange} placeholder="Ex: Brun" disabled={isViewMode} />
            </div>
          )}
          {showFrameFields && (
            <div className="col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6 border-t pt-4 mt-4">
              <div className="font-semibold text-primary-700 md:col-span-2">Détails Monture (Optionnel)</div>
              <ModernInput label="Taille de la monture" name="frame_size" value={form.frame_size} onChange={handleChange} placeholder="Ex: 54" disabled={isViewMode} />
              <ModernInput label="Taille du pont" name="bridge_size" value={form.bridge_size} onChange={handleChange} placeholder="Ex: 18" disabled={isViewMode} />
              <ModernInput label="Longueur des branches" name="temple_length" value={form.temple_length} onChange={handleChange} placeholder="Ex: 140" disabled={isViewMode} />
              <ModernInput label="Matériau monture" name="frame_material" value={form.frame_material} onChange={handleChange} placeholder="Ex: Acétate" disabled={isViewMode} />
              <ModernInput label="Forme monture" name="frame_shape" value={form.frame_shape} onChange={handleChange} placeholder="Ex: Carrée" disabled={isViewMode} />
              <ModernInput label="Couleur monture" name="frame_color" value={form.frame_color} onChange={handleChange} placeholder="Ex: Noir" disabled={isViewMode} />
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4">
            {isViewMode ? (
              <ModernButton
                type="button"
                variant="secondary"
                onClick={() => setShowForm(false)}
              >
                Fermer
              </ModernButton>
            ) : (
              <>
                <ModernButton
                  type="button"
                  variant="secondary"
                  onClick={() => setShowForm(false)}
                >
                  Annuler
                </ModernButton>
                <ModernButton
                  type="submit"
                  variant="primary"
                  icon={<Save className="w-4 h-4" />}
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Enregistrement...' : (editingId ? 'Modifier' : 'Ajouter')}
                </ModernButton>
              </>
            )}
          </div>
        </form>
      </ModernModal>

      {/* Barcode Print Modal */}
      <ModernModal
        isOpen={showBarcodeModal}
        onClose={() => setShowBarcodeModal(false)}
        title={barcodeProduct ? `Imprimer code-barres: ${barcodeProduct.name}` : 'Imprimer code-barres'}
        size="sm"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Nombre d'étiquettes à imprimer</label>
            <input
              type="number"
              min={1}
              value={barcodeCount}
              onChange={e => setBarcodeCount(Number(e.target.value))}
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div className="flex items-center justify-end space-x-2">
            <ModernButton variant="secondary" onClick={() => setShowBarcodeModal(false)}>Annuler</ModernButton>
            <ModernButton
              variant="primary"
              onClick={generateBarcodePDF}
            >
              Générer PDF
            </ModernButton>
          </div>
        </div>
      </ModernModal>

      {/* Barcode Selection Modal */}
      <ModernModal
        isOpen={showBarcodeSelectionModal}
        onClose={() => setShowBarcodeSelectionModal(false)}
        title="Sélectionner les produits pour codes-barres"
        size="xl"
      >
        <div className="space-y-6">
          {/* Search Section */}
          <div>
            <ModernSearchBar
              placeholder="Rechercher des produits..."
              onSearch={setBarcodeSearchTerm}
              className="w-full"
              debounce={300}
            />
          </div>

          {/* Search Results */}
          {barcodeSearchTerm && (
            <div>
              <h4 className="font-semibold text-neutral-900 mb-3">Résultats de recherche</h4>
              <div className="max-h-64 overflow-y-auto space-y-2">
                {products
                  .filter(product => 
                    !selectedProductsForBarcode.find(p => p.id === product.id) &&
                    (product.name?.toLowerCase().includes(barcodeSearchTerm.toLowerCase()) ||
                     product.brand?.toLowerCase().includes(barcodeSearchTerm.toLowerCase()) ||
                     product.model?.toLowerCase().includes(barcodeSearchTerm.toLowerCase()) ||
                     product.barcode?.toLowerCase().includes(barcodeSearchTerm.toLowerCase()))
                  )
                  .map(product => (
                    <div
                      key={product.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-neutral-50 cursor-pointer"
                      onClick={() => addProductToBarcodeSelection(product)}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                          <Package className="w-4 h-4 text-primary-600" />
                        </div>
                        <div>
                          <div className="font-medium text-neutral-900">{product.name}</div>
                          <div className="text-sm text-neutral-500">
                            {product.brand && `${product.brand} `}
                            {product.model && `- ${product.model}`}
                          </div>
                          <div className="text-sm text-neutral-600">
                            Prix: {formatCurrency(product.sale_price)}
                            {product.barcode && ` | Code: ${product.barcode}`}
                          </div>
                        </div>
                      </div>
                      <ModernButton
                        variant="ghost"
                        size="sm"
                        icon={<Plus className="w-4 h-4" />}
                      >
                        Ajouter
                      </ModernButton>
                    </div>
                  ))}
                {products
                  .filter(product => 
                    !selectedProductsForBarcode.find(p => p.id === product.id) &&
                    (product.name?.toLowerCase().includes(barcodeSearchTerm.toLowerCase()) ||
                     product.brand?.toLowerCase().includes(barcodeSearchTerm.toLowerCase()) ||
                     product.model?.toLowerCase().includes(barcodeSearchTerm.toLowerCase()) ||
                     product.barcode?.toLowerCase().includes(barcodeSearchTerm.toLowerCase()))
                  ).length === 0 && barcodeSearchTerm && (
                  <div className="text-center py-8 text-neutral-500">
                    Aucun produit trouvé pour "{barcodeSearchTerm}"
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Selected Products */}
          <div>
            <h4 className="font-semibold text-neutral-900 mb-3">Produits sélectionnés</h4>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {selectedProductsForBarcode.map(product => (
                <div
                  key={product.id}
                  className="p-3 border rounded-lg bg-primary-50"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                        <Package className="w-4 h-4 text-primary-600" />
                      </div>
                      <div>
                        <div className="font-medium text-neutral-900">{product.name}</div>
                        <div className="text-sm text-neutral-500">
                          {product.brand && `${product.brand} `}
                          {product.model && `- ${product.model}`}
                        </div>
                      </div>
                    </div>
                    <ModernButton
                      variant="ghost"
                      size="sm"
                      icon={<X className="w-4 h-4" />}
                      onClick={() => removeProductFromBarcodeSelection(product.id)}
                    >
                      Retirer
                    </ModernButton>
                  </div>
                  <div className="flex items-center space-x-3">
                    <label className="text-sm font-medium text-neutral-700">Quantité:</label>
                    <input
                      type="number"
                      min="1"
                      value={product.quantity || 1}
                      onChange={(e) => updateProductQuantity(product.id, Number(e.target.value))}
                      className="w-20 border rounded px-2 py-1 text-sm"
                    />
                    <span className="text-sm text-neutral-600">
                      Prix: {formatCurrency(product.sale_price)}
                    </span>
                  </div>
                </div>
              ))}
              {selectedProductsForBarcode.length === 0 && (
                <div className="text-center py-8 text-neutral-500">
                  Aucun produit sélectionné
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-sm text-neutral-600">
              {selectedProductsForBarcode.length} produit(s) sélectionné(s)
            </div>
            <div className="flex items-center space-x-2">
              <ModernButton
                variant="secondary"
                onClick={() => setShowBarcodeSelectionModal(false)}
              >
                Annuler
              </ModernButton>
              <ModernButton
                variant="primary"
                icon={<Package className="w-4 h-4" />}
                onClick={generateMultipleBarcodePDF}
                disabled={selectedProductsForBarcode.length === 0}
              >
                Générer PDF ({selectedProductsForBarcode.reduce((total, p) => total + (p.quantity || 1), 0)} étiquettes)
              </ModernButton>
            </div>
          </div>
        </div>
      </ModernModal>
    </div>
  );
};

export default Products; 